import { useRef, useEffect } from 'react';
import type { ComputedRoute } from '../types/api';

const M_TO_FT = 3.28084;
const M_TO_MI = 0.000621371;

interface ElevationProfileProps {
  route: ComputedRoute;
  color: string;
}

export function ElevationProfile({ route, color }: ElevationProfileProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const padding = { top: 14, bottom: 16, left: 36, right: 8 };

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    const profile = route.elevation_profile;
    if (!profile || profile.length < 2) return;

    const hasRealData = profile.some(p => p.elevation_m !== 0);
    if (!hasRealData) {
      ctx.fillStyle = '#8888aa';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Elevation data loading...', width / 2, height / 2);
      return;
    }

    const distances = profile.map(p => p.dist_m);
    const elevations = profile.map(p => p.elevation_m);

    const minElev = Math.min(...elevations);
    const maxElev = Math.max(...elevations);
    const range = maxElev - minElev || 1;
    const totalDist = distances[distances.length - 1] || 1;

    const plotW = width - padding.left - padding.right;
    const plotH = height - padding.top - padding.bottom;

    ctx.strokeStyle = '#2a2a4e';
    ctx.lineWidth = 0.5;
    const elevStep = niceStep(range * M_TO_FT, 4);
    const minElevFt = Math.floor(minElev * M_TO_FT / elevStep) * elevStep;
    const maxElevFt = minElevFt + Math.ceil((maxElev - minElev) * M_TO_FT / elevStep) * elevStep;
    const elevRangeFt = maxElevFt - minElevFt || 1;

    for (let ft = minElevFt; ft <= maxElevFt; ft += elevStep) {
      const y = padding.top + plotH * (1 - (ft - minElevFt) / elevRangeFt);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }

    ctx.beginPath();
    for (let i = 0; i < distances.length; i++) {
      const x = padding.left + (distances[i] / totalDist) * plotW;
      const y = padding.top + plotH * (1 - (elevations[i] - minElev) / range);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();

    const lastX = padding.left + plotW;
    ctx.lineTo(lastX, padding.top + plotH);
    ctx.lineTo(padding.left, padding.top + plotH);
    ctx.closePath();
    const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + plotH);
    gradient.addColorStop(0, color + '40');
    gradient.addColorStop(1, color + '05');
    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.fillStyle = '#8888aa';
    ctx.font = '9px monospace';
    ctx.textAlign = 'right';
    for (let ft = minElevFt; ft <= maxElevFt; ft += elevStep) {
      const y = padding.top + plotH * (1 - (ft - minElevFt) / elevRangeFt);
      ctx.fillText(`${ft.toFixed(0)}`, padding.left - 3, y + 3);
    }

    ctx.textAlign = 'center';
    const distMi = totalDist * M_TO_MI;
    const distStep = niceStep(distMi, 4);
    for (let mi = 0; mi <= distMi; mi += distStep) {
      const x = padding.left + (mi / distMi) * plotW;
      ctx.fillText(`${mi.toFixed(1)}`, x, height - 2);
    }
  }, [route, color]);

  return (
    <div className="elevation-profile">
      <canvas ref={canvasRef} style={{ width: '100%', height: '80px' }} />
    </div>
  );
}

function niceStep(range: number, targetTicks: number): number {
  const rough = range / targetTicks;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / mag;
  let step: number;
  if (norm <= 1.5) step = 1;
  else if (norm <= 3) step = 2;
  else if (norm <= 7) step = 5;
  else step = 10;
  return step * mag;
}
